import { PermissionsGroups } from '@core/permissions/permissions.group';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { ListGroupTask } from '@shared/modules/list/model/list-group-task.model';
import { TranslateService } from '@ngx-translate/core';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import {
  ListTaskEvent,
  ListTaskEventType,
  ListTaskService
} from '@shared/modules/list/services/list-task.service';
import { Subscription } from 'rxjs';
import { ListTasksSingleTaskComponent } from '@shared/modules/list/components/list-tasks/components/list-tasks-single-task/list-tasks-single-task.component';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { Task } from '@shared/models/task.model';
import { ListService } from '@shared/modules/list/services/list.service';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { ListEvent, ListEventType } from '@shared/modules/list/model/list-event.model';
import { Config } from '@shared/configs/config';
import { TaskGroupModalComponent } from '@shared/modules/task-sidenav/components/task-group-modal/task-group-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ListGroupTranslatePipe } from '@shared/modules/list/pipes/list-group-translate.pipe';
import { TaskGroup } from '@shared/models/task-group.model';
import { ProjectTaskAPIService } from '@modules/projects/shared/services/project-task-api.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { HttpError } from '@shared/interfaces/error.interface';
import { IntilioCodes } from '@shared/enums/initilio-codes.enum';
import { DndDropEvent } from 'ngx-drag-drop';
import { StorageService } from '@core/services/storage.service';
import { ActivatedRoute } from '@angular/router';
import { map, shareReplay, startWith, take } from 'rxjs/operators';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { TaskType } from '@shared/enums/task-type.enum';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';

@Component({
  selector: 'list-tasks-group',
  templateUrl: './list-tasks-group.component.html',
  styleUrls: ['./list-tasks-group.component.scss'],
  host: { class: 'list-tasks-group' }
})
export class ListTasksGroupComponent implements OnInit, AfterViewInit, OnDestroy {
  TaskStatus = TaskStatus;
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  PermissionsGroups = PermissionsGroups;

  initEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  sub: Subscription = new Subscription();
  taskGroupId: string | number;

  listGroupTask: ListGroupTask;
  isEmpty: boolean = false;
  isEmptyCompleted: boolean = false;
  isEmptyCancelled: boolean = false;

  dragging: boolean = false;
  interval = null;
  mousePosition = { x: 0, y: 0 };

  @Input() dragPermissions: any = {};

  @ViewChild('cancelledContainer', { read: ViewContainerRef }) cancelledContainer: ViewContainerRef;
  @ViewChild('completedContainer', { read: ViewContainerRef }) completedContainer: ViewContainerRef;
  @ViewChild('tasksContainer', { read: ViewContainerRef }) tasksContainer: ViewContainerRef;

  @ViewChildren('tasksComponents') tasksComponents: QueryList<ListTasksSingleTaskComponent>;

  readonly isDelegatedTasksTab$ = this.activatedRoute.queryParamMap.pipe(
    startWith(this.activatedRoute.snapshot.queryParamMap),
    map((p) => p.get('delegatedTasks') === '1'),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private t: TranslateService,
    private s: SnackBarService,
    public changes: ChangeDetectorRef,
    public listTaskService: ListTaskService,
    public listService: ListService,
    public elRef: ElementRef,
    public renderer: Renderer2,
    private ngZone: NgZone,
    private dialog: MatDialog,
    private listGroupTranslatePipe: ListGroupTranslatePipe,
    private projectTaskApiService: ProjectTaskAPIService,
    public service: ListService,
    public store: StorageService,
    public resolver: ComponentFactoryResolver,
    public zone: NgZone,
    public activatedRoute: ActivatedRoute,
    private taskService: TaskService,
    public taskSidenavService: TaskSidenavService
  ) {}

  ngOnInit(): void {
    this.listenListTaskEvents();
    this.listenListServiceEvents();
    this.setListGroupTask();
  }

  ngAfterViewInit() {
    this.initEmitter.emit(true);
  }

  dragstart() {
    // Workaround: proper display task tile while dragging and sidebar is open
    const sidebar = document.querySelector('.sidenav');
    if (sidebar) {
      sidebar?.classList.add('d-none');
      setTimeout(() => sidebar?.classList.remove('d-none'), 0);
    }

    this.dragging = true;
    this.scrollInterval();
    this.changes.detectChanges();
  }

  drag(e: DragEvent) {
    this.mousePosition = Object.assign({}, { x: e.clientX, y: e.clientY });
    this.changes.detectChanges();
  }

  dragend() {
    this.dragging = false;
    this.changes.detectChanges();
  }

  scrollInterval() {
    const offset = 200;
    const speedOffset = 200;

    const element = document.querySelector('.base-panel');
    const _scrollElement: any = element;

    this.interval = setInterval(() => {
      const mousePositionY = this.mousePosition.y;
      if (!this.dragging) {
        clearInterval(this.interval);
        this.interval = null;
      }
      const currentScrollTop = _scrollElement.scrollTop;

      // Scrolling Down
      if (mousePositionY > _scrollElement.clientHeight - offset) {
        _scrollElement.scrollTo(_scrollElement.scrollLeft, currentScrollTop + speedOffset);
      }

      //Scrolling Up
      if (this.mousePosition.y < offset) {
        _scrollElement.scrollTo(_scrollElement.scrollLeft, currentScrollTop - speedOffset);
      }
      this.changes.detectChanges();
    }, 200);
  }

  setListGroupTask() {
    this.listGroupTask = this.listTaskService.listGroupTasksHashMap.get(
      `${ListTaskService.LIST_GROUP_TASK_PREFIX}${this.taskGroupId}`
    );
    this.setIsEmpty();
  }

  listenListTaskEvents() {
    const sub = this.listTaskService.emitter.subscribe((event: ListTaskEvent) => {
      switch (event.type) {
        case ListTaskEventType.REFRESH_LIST_GROUPS:
          this.setListGroupTask();
          this.changes.detectChanges();
          break;
      }
    });
    this.sub.add(sub);
  }

  listenListServiceEvents() {
    const sub = this.listService.eventEmitter.subscribe((event: ListEvent<any>) => {
      switch (event.type) {
        case ListEventType.END_GET_ROWS:
          this.setListGroupTask();
          this.changes.detectChanges();
          break;
      }
    });
    this.sub.add(sub);
  }

  addTask(taskGroup: TaskGroup) {
    const userEmployeeId = this.store.UserPerson?.employees?.[0]?.id;
    this.ngZone.run(() => {
      const taskSidenavController = new TaskSidenavController();
      taskSidenavController.addTask({
        projectID: this.listTaskService.projectId,
        taskGroupId: Number(this.taskGroupId),
        taskType: this.activatedRoute.snapshot.data.type || TaskType.TYPE_TASK,
        groupType: taskGroup.type || null,
        assignToIds: userEmployeeId && this.listTaskService.isMyWorkTaskLists ? [userEmployeeId] : []
      });
    });
  }

  setIsEmpty(): void {
    const foreachLogic = (map: Map<string, Task>, status: TaskStatus): boolean => {
      let localFlag = true;

      map.forEach((task: Task, key: string) => {
        const isConsideredForDisplay =
          !task.parentId ||
          (task.parentId &&
            this.listTaskService.isMyWorkTaskLists &&
            this.getParent(task)?.workspaceTaskGroup?.id !== task.workspaceTaskGroup?.id) ||
          (!this.listTaskService.isMyWorkTaskLists &&
            this.getParent(task)?.taskGroup?.id !== task.taskGroup?.id);

        if (isConsideredForDisplay) {
          if (this.listTaskService.isMyWorkTaskLists) {
            if (task.status === status && task.workspaceTaskGroup?.id === this.taskGroupId) {
              localFlag = false;
            }
          } else {
            if (task.status === status && task.taskGroup?.id === this.taskGroupId) {
              localFlag = false;
            }
          }
        }
      });

      return localFlag;
    };

    this.isEmpty = foreachLogic(this.listTaskService.tasksHashMap, TaskStatus.CREATED);
    this.isEmptyCompleted = foreachLogic(this.listTaskService.completedTasksHashMap, TaskStatus.COMPLETED);
    this.isEmptyCancelled = foreachLogic(this.listTaskService.cancelledTasksHashMap, TaskStatus.CANCELLED);
  }

  private getParent(task: Task): Task | undefined {
    if (!task?.parentId) return undefined;
    return this.listTaskService.getTask(task.parentId);
  }

  editGroupName(listGroupTask: ListGroupTask) {
    const dialog = this.dialog.open(TaskGroupModalComponent, {
      width: Config.DEFAULT_MODAL_WIDTH,
      autoFocus: true,
      data: { notSendNewGroup: true, edit: true, taskGroup: listGroupTask?.taskGroup }
    });

    dialog.componentInstance.group.setValue(
      this.listGroupTranslatePipe.transform(listGroupTask.taskGroup.name)
    );

    const sub = dialog.componentInstance.onGroupAdd.subscribe((groupName: string) => {
      const groupId = listGroupTask?.taskGroup?.id;
      listGroupTask.taskGroup.name = groupName;

      const edit$ = this.listTaskService.isMyWorkTaskLists
        ? this.taskService.editWorkspaceTaskGroupName(groupName, groupId)
        : this.taskService.editTaskGroup(groupName, groupId);

      edit$.subscribe(() => {
        this.changes.detectChanges();
        sub.unsubscribe();
      });
    });

    const subDelete = dialog.componentInstance.onGroupRemove.subscribe((group: TaskGroup) => {
      const onSuccess = () => {
        this.listService.getRows().add(() => {
          this.listService.eventEmitter.emit({ type: ListEventType.ROW_REMOVED, data: null });
          this.changes.detectChanges();
        });
        this.changes.detectChanges();
        this.s.success(this.t.instant('Projects.groupRemoved'));
      };

      const onError = (error: HttpError) => {
        let text = 'errorDefault';
        if (error.messageCode === IntilioCodes.GROUP_NOT_EMPTY) {
          text = 'Projects.groupNotEmpty';
        }
        this.s.error(this.t.instant(text));
      };

      if (this.listTaskService.isMyWorkTaskLists) {
        this.projectTaskApiService
          .removeWorkspaceGroup(group.id)
          .pipe(take(1))
          .subscribe({
            next: onSuccess,
            error: onError,
            complete: () => {
              this.changes.detectChanges();
              subDelete.unsubscribe();
            }
          });
      } else {
        this.projectTaskApiService
          .taskGroupBulkEdit({
            groupsToDeleteIds: [group.id],
            projectId: this.listTaskService?.projectId,
            type: this.activatedRoute.snapshot.data.type
          })
          .subscribe({
            next: onSuccess,
            error: onError,
            complete: () => {
              this.changes.detectChanges();
              subDelete.unsubscribe();
            }
          });
      }
    });
  }

  dropEvent(event: DndDropEvent, targetGroup: ListGroupTask) {
    const isInitialGroupProject = event.data?.workspaceTaskGroup?.type === 'project';

    if (this.listTaskService.isMyWorkTaskLists && targetGroup.withProject !== isInitialGroupProject) {
      this.ngZone.run(() => this.s.error(this.t.instant('Projects.actionForbidden')));
      return;
    }

    this.listTaskService.emitter.emit({
      type: ListTaskEventType.UPDATE_TASK_ORDER,
      data: { event, targetGroup, index: event.index }
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
